import * as React from "react";
import { Hint, Toast } from "react-ui";

import { IconButton } from "../IconButton/IconButton";
import { MenuItem } from "../MenuItem/MenuItem";
import { useMetrics } from "../../../../Internals/Metrics/MetricsProviderContext";
import {
    UNIT_MENU_ADD_CLICK,
    UNIT_MENU_CLEAR_CLICK,
    UNIT_MENU_CLICK,
    UNIT_MENU_COPY_CLICK,
    UNIT_MENU_REMOVE_CLICK,
} from "../../../../metricsAction";
import { UnitId, unitIdToLegacyUnitId, unitIdWithInstance } from "../../../Unit/UnitId/UnitId";
import { AbsoluteResolvedModelPath } from "../../../../../../../Common/ModelPath/ModelPath";
import { declint } from "../../../../../Engine/utils";
import { arrayHasAtLeastOneItem, noop } from "../../../../../../../Common/TypingUtils";
import { TestPageObjectHandle } from "../../../../Services/TestPageObjects/TestPageObjectHandle";
import { NestedLevelContext } from "../../NestedLevelContext";
import { PropsPageActions } from "../../../Unit/Types";
import { getMenuItemInfosKey } from "../../UnitItemHelpers";
import { getFromLocalStorage } from "../../../../../Engine/Core/Store/LocalStorageUtils";
import { ErrorsCount } from "../../../../Commons/Components/ErrorsCount";
import { useFormSettings } from "../../../../FormObjectProvider";

import { NestedItems } from "./NestedItems.styles";
import { MultipleUnitPagingContainer } from "./MultipleUnitPaging/MultipleUnitPagingContainer";
import { MenuItemChildInstanceContainer as MenuItemChildInstance } from "./MenuItemChildInstance/MenuItemChildInstanceContainer";
import { PagingWrapper, Description } from "./MultipleUnitPaging/MultipleUnitPaging.styles";

export interface MultipleUnitProps {
    id: UnitId;
    unitPath: AbsoluteResolvedModelPath;
    navigationLimit: number;
    name?: string;
    instances: readonly string[];
    parentInstances?: string[];
    optional?: boolean;
    filled?: boolean;
    hideActions: boolean;
    pageActions?: PropsPageActions;
    isChildItemActive: boolean;
    currentInstance: undefined | string;
    children?: React.ReactNode;
    isReadOnly?: boolean;
    hasSingleChild?: boolean;
    isOnlyPlus: boolean;
    maxOccurs?: number;
    fufDraftId: string;
    errorsCount: number;
    onGoToUnit: (id: UnitId) => void;
    onAddInstance: () => void;
    onCopyInstance: (instance: string) => Promise<void>;
    onRemoveInstance: (instance: string, instanceIndex: number) => void;
    onClearInstance: (instance: string, instanceTitle: string) => void;
    getInstanceTitle: (instance: string, instanceIndex: number) => string;
    getInstanceExtraInfo: (instance: string) => string | undefined;
    setCollapsedUnitItem: (isCollapsed: boolean, fufDraftId?: string, instance?: string, instances?: string[]) => void;
}

const MultipleUnitPaging = PagingWrapper.withComponent(MultipleUnitPagingContainer);

export function MultipleUnit(props: MultipleUnitProps): React.ReactElement {
    const track = useMetrics();
    const { showErrorsCountInPanel } = useFormSettings();
    const nestedLevel = React.useContext(NestedLevelContext);
    const [collapsed, setCollapsed] = React.useState<boolean>(
        getFromLocalStorage<boolean>(
            getMenuItemInfosKey(
                props.id.pageId,
                props.fufDraftId,
                props.currentInstance,
                props.parentInstances,
                props.name
            ),
            false
        )
    );
    const isRenderAsPager = props.instances.length > props.navigationLimit;
    const isHideRemoveAction = !props.optional && props.instances.length === 1;
    const testPageObjectId = `UnitItem-${props.id?.pageId ?? "none"}`;
    const disableAppend = props.maxOccurs != undefined && props.instances.length >= props.maxOccurs;

    const handleSelectInstance = (instance: string): void => {
        const unitId = unitIdWithInstance(props.id, instance);
        track(UNIT_MENU_CLICK, { unit: unitIdToLegacyUnitId(unitId) });
        props.onGoToUnit(unitId);
    };

    const handleClickUnitHeader = () => {
        if (!props.isChildItemActive) {
            if (arrayHasAtLeastOneItem(props.instances)) {
                const unitId = unitIdWithInstance(props.id, props.currentInstance ?? props.instances[0]);
                props.onGoToUnit(unitId);
            } else {
                props.onGoToUnit(props.id);
            }
        }
    };

    const handleAddInstance = (instance: string): void => {
        const unitId = unitIdWithInstance(props.id, instance);
        track(UNIT_MENU_ADD_CLICK, { unit: unitIdToLegacyUnitId(unitId) });
        props.onAddInstance();
    };

    const handleCopyInstance = async (instance: string): Promise<void> => {
        const unitId = unitIdWithInstance(props.id, instance);
        track(UNIT_MENU_COPY_CLICK, { unit: unitIdToLegacyUnitId(unitId) });
        Toast.push("Раздел скопирован");
        await props.onCopyInstance(instance);
    };

    const handleClearInstance = (instance: string, instanceIndex: number): void => {
        const unitId = unitIdWithInstance(props.id, instance);
        track(UNIT_MENU_CLEAR_CLICK, { unit: unitIdToLegacyUnitId(unitId) });
        props.onClearInstance(instance, instanceIndex.toString());
    };

    const handleRemoveInstance = (instance: string, instanceIndex: number): void => {
        const unitId = unitIdWithInstance(props.id, instance);
        track(UNIT_MENU_REMOVE_CLICK, { unit: unitIdToLegacyUnitId(unitId) });
        props.onRemoveInstance(instance, instanceIndex);
    };

    const testPageObject = React.useMemo(
        () => ({
            getType: () => "MultipleUnit",
            add: props.onAddInstance,
            isCompacted: () => isRenderAsPager,
            count: () => props.instances.length,
        }),
        [isRenderAsPager, props.instances]
    );

    const getActions = () => {
        if (props.isReadOnly || props.hideActions) {
            return null;
        }
        return (
            <Hint
                useWrapper={false}
                pos="bottom left"
                text={disableAppend ? "Добавлено максимальное количество экземпляров" : "Добавить экземпляр"}>
                <IconButton
                    data-tid="Add"
                    data-page-id={props.id.pageId}
                    icon="PlusIcon"
                    onClick={props.onAddInstance}
                    disabled={disableAppend}
                />
            </Hint>
        );
    };

    if (props.isReadOnly && !props.filled) {
        return <></>;
    }

    if (isRenderAsPager) {
        const currentInstance = props.currentInstance;
        const currentInstanceIndex = currentInstance ? props.instances.findIndex(x => x === currentInstance) : 0;
        const showLabel = showErrorsCountInPanel && props.errorsCount > 0;
        const label = showLabel && <ErrorsCount value={props.errorsCount} style={{ marginLeft: "5px" }} />;

        return (
            <TestPageObjectHandle id={testPageObjectId} pageObject={testPageObject}>
                <MenuItem
                    id={props.id}
                    data-tid="Root"
                    data-page-id={props.id.pageId}
                    empty={!props.filled}
                    childActive={props.isChildItemActive}
                    onClick={handleClickUnitHeader}
                    action={getActions()}
                    currentInstance={currentInstance}
                    setCollapsedUnitItem={props.setCollapsedUnitItem}
                    parentInstances={props.parentInstances}
                    fufDraftId={props.fufDraftId}
                    label={label}
                    content={
                        isRenderAsPager &&
                        (props.isChildItemActive ? (
                            <MultipleUnitPaging
                                currentInstance={props.currentInstance}
                                id={props.id}
                                instances={props.instances}
                                onGoToUnit={props.onGoToUnit}
                            />
                        ) : (
                            <Description style={{ paddingLeft: nestedLevel * 16 }}>
                                {declint(props.instances.length, ["%s экземпляр", "%s экземпляра", "%s экземпляров"])}
                            </Description>
                        ))
                    }
                    name={props.name}>
                    {currentInstance != undefined && (
                        <NestedItems>
                            <MenuItemChildInstance
                                key={currentInstance}
                                id={props.id}
                                index={currentInstanceIndex}
                                unitPath={props.unitPath}
                                instance={currentInstance}
                                instanceIndex={currentInstanceIndex}
                                isChildItemActive={props.isChildItemActive}
                                isHideRemoveAction={isHideRemoveAction}
                                pageActions={props.pageActions}
                                hideActions={props.hideActions}
                                onSelectInstance={noop}
                                onAddInstance={() => handleAddInstance(currentInstance)}
                                onCopyInstance={async () => await handleCopyInstance(currentInstance)}
                                onRemoveInstance={() => handleRemoveInstance(currentInstance, currentInstanceIndex)}
                                onClearInstance={() => handleClearInstance(currentInstance, currentInstanceIndex)}
                                getInstanceTitle={props.getInstanceTitle}
                                getInstanceExtraInfo={props.getInstanceExtraInfo}
                                isOnlyPlus={props.isOnlyPlus}
                                disableAppend={disableAppend}
                                setCollapsedUnitItem={props.setCollapsedUnitItem}
                                fufDraftId={props.fufDraftId}
                                parentInstances={props.parentInstances}
                                name={props.name}
                                showErrorsCount={showErrorsCountInPanel}>
                                {props.children}
                            </MenuItemChildInstance>
                        </NestedItems>
                    )}
                </MenuItem>
            </TestPageObjectHandle>
        );
    } else {
        return (
            <TestPageObjectHandle id={testPageObjectId} pageObject={testPageObject}>
                <MenuItem
                    data-tid="Root"
                    id={props.id}
                    data-page-id={props.id.pageId}
                    empty={!props.filled}
                    childActive={props.isChildItemActive}
                    collapsible={props.filled && true}
                    collapsed={collapsed}
                    onCollapseChange={setCollapsed}
                    action={getActions()}
                    setCollapsedUnitItem={props.setCollapsedUnitItem}
                    name={props.name}
                    count={!isRenderAsPager ? props.instances.length : 0}
                    parentInstances={props.parentInstances}
                    fufDraftId={props.fufDraftId}
                    onClick={handleClickUnitHeader}>
                    {!collapsed && (
                        <NestedItems>
                            <NestedLevelContext.Provider value={nestedLevel + 1}>
                                {props.instances.map((instance, index) => (
                                    <MenuItemChildInstance
                                        key={instance}
                                        id={props.id}
                                        hideActions={props.hideActions}
                                        pageActions={props.pageActions}
                                        index={index}
                                        unitPath={props.unitPath}
                                        instance={instance}
                                        instanceIndex={index}
                                        isChildItemActive={props.isChildItemActive}
                                        isHideRemoveAction={isHideRemoveAction}
                                        onSelectInstance={() => handleSelectInstance(instance)}
                                        onAddInstance={() => handleAddInstance(instance)}
                                        onCopyInstance={async () => await handleCopyInstance(instance)}
                                        onClearInstance={() => handleClearInstance(instance, index)}
                                        onRemoveInstance={() => handleRemoveInstance(instance, index)}
                                        getInstanceTitle={props.getInstanceTitle}
                                        getInstanceExtraInfo={props.getInstanceExtraInfo}
                                        isOnlyPlus={props.isOnlyPlus}
                                        disableAppend={disableAppend}
                                        setCollapsedUnitItem={props.setCollapsedUnitItem}
                                        parentInstances={props.parentInstances}
                                        fufDraftId={props.fufDraftId}
                                        name={props.name}>
                                        {props.children}
                                    </MenuItemChildInstance>
                                ))}
                            </NestedLevelContext.Provider>
                        </NestedItems>
                    )}
                </MenuItem>
            </TestPageObjectHandle>
        );
    }
}
